import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";
import { track } from "../lib/analytics.js";

const API_HOST_RAW: string | undefined = (
  import.meta as unknown as { env: { VITE_API_HOST?: string } }
).env.VITE_API_HOST;
const API_HOST: string = (API_HOST_RAW ?? "https://vaultbreaker-api.fly.dev").replace(/\/$/, "");

/** When VITE_API_HOST is set we hit the absolute URL directly. Otherwise we
 *  let Vercel rewrite /public/* to the game-server (vercel.json may not have
 *  the rewrite — in that case the relative URL just 404s and we surface a
 *  friendly error). */
const SANDBOX_ENDPOINT: string = API_HOST_RAW
  ? `${API_HOST}/public/sandbox/operator`
  : "/public/sandbox/operator";

interface SandboxSuccess {
  operatorId: string;
  apiKey: string;
  expiresAt: string;
  sandboxApiHost?: string;
  integrationGuide?: string;
}

interface FormState {
  companyName: string;
  email: string;
  country: string;
  website: string;
}

const EMPTY: FormState = {
  companyName: "",
  email: "",
  country: "",
  website: "",
};

/** Same curated list Contact.tsx uses — kept in sync by hand for now (small
 *  enough to inline here without pulling Contact's COUNTRY_OPTIONS into a
 *  shared module before Agent 6 owns lib/seo.tsx). */
const COUNTRY_OPTIONS: string[] = [
  "Malta",
  "Gibraltar",
  "Isle of Man",
  "Curaçao",
  "United Kingdom",
  "Estonia",
  "Sweden",
  "Germany",
  "Netherlands",
  "Spain",
  "Italy",
  "France",
  "Cyprus",
  "Israel",
  "United States",
  "Canada",
  "Brazil",
  "Mexico",
  "Argentina",
  "Peru",
  "Chile",
  "Colombia",
  "India",
  "Philippines",
  "Singapore",
  "Hong Kong",
  "Australia",
  "New Zealand",
  "South Africa",
  "Nigeria",
  "United Arab Emirates",
  "Turkey",
  "Switzerland",
  "Ireland",
  "Denmark",
  "Finland",
  "Norway",
  "Other",
];

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; data: SandboxSuccess; submitted: FormState }
  | { kind: "error"; message: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Sandbox() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const onChange =
    <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
    };

  function validate(state: FormState): string | null {
    if (state.companyName.trim().length < 2) return "Company name is required.";
    if (!EMAIL_RE.test(state.email.trim())) return "Enter a valid work email.";
    if (state.country.trim().length === 0) return "Pick a country.";
    if (state.website.trim().length > 0) {
      const w = state.website.trim();
      if (!/^https?:\/\//i.test(w) && !/^[\w.-]+\.[a-z]{2,}/i.test(w)) {
        return "Website should look like a URL or a domain.";
      }
    }
    return null;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const error = validate(form);
    if (error) {
      setStatus({ kind: "error", message: error });
      return;
    }
    setStatus({ kind: "submitting" });

    const payload: Record<string, string> = {
      companyName: form.companyName.trim(),
      email: form.email.trim(),
      country: form.country.trim(),
    };
    if (form.website.trim().length > 0) payload.website = form.website.trim();

    try {
      const res = await fetch(SANDBOX_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        if (res.status === 429) {
          setStatus({
            kind: "error",
            message: "Too many sandbox sign-ups from your IP — try again in a few minutes.",
          });
          return;
        }
        if (res.status === 409) {
          setStatus({
            kind: "error",
            message:
              "A sandbox key already exists for that email. Check your inbox or contact support.",
          });
          return;
        }
        setStatus({
          kind: "error",
          message: `Sign-up rejected (${res.status}). Please try again or email hello@vaultbreaker.dev.`,
        });
        return;
      }
      const data = (await res.json()) as SandboxSuccess;
      setStatus({ kind: "success", data, submitted: form });
      track("sandbox_signup", {
        country: form.country,
        hasWebsite: form.website.trim().length > 0,
      });
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Network error.",
      });
    }
  }

  if (status.kind === "success") {
    return <SuccessView data={status.data} />;
  }

  return (
    <>
      <Seo
        title="Sandbox · Vault Breaker Studios"
        description="Get a free sandbox API key for Vault Breaker. Try the integration before talking to sales — keys expire in 14 days."
        path="/sandbox"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          SANDBOX
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(40px, 5.5vw, 64px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Get a sandbox API key
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--fg-2)",
            marginTop: 14,
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          Try the integration before talking to us. Keys expire in 14 days. Test data only.
        </p>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 720 }}>
        <Card style={{ padding: 28 }}>
          <form onSubmit={onSubmit} style={{ display: "grid", gap: 16 }}>
            <Field label="Company name" required>
              <input
                value={form.companyName}
                onChange={onChange("companyName")}
                className="vb-text-input"
                required
                minLength={2}
                placeholder="Acme Casino"
              />
            </Field>

            <Field label="Work email" required>
              <input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="vb-text-input"
                required
                placeholder="you@acmecasino.com"
              />
            </Field>

            <Field label="Country" required>
              <select
                value={form.country}
                onChange={onChange("country")}
                className="vb-text-input"
                required
              >
                <option value="" disabled>
                  Select a jurisdiction…
                </option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Website (optional)">
              <input
                value={form.website}
                onChange={onChange("website")}
                className="vb-text-input"
                placeholder="https://acmecasino.com"
              />
            </Field>

            {status.kind === "error" ? (
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px solid rgba(255,59,71,0.35)",
                  background: "var(--red-wash)",
                  color: "var(--red)",
                  fontSize: 13,
                  borderRadius: 10,
                }}
              >
                {status.message}
              </div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
                Sandbox is rate-limited. For production access, contact sales.
              </span>
              <Button
                variant="gold"
                type="submit"
                disabled={status.kind === "submitting"}
                style={{ padding: "14px 22px", fontSize: 15 }}
              >
                {status.kind === "submitting" ? "ISSUING…" : "GET SANDBOX KEY"}
              </Button>
            </div>
          </form>
        </Card>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 720 }}>
        <Card style={{ padding: 22, display: "grid", gap: 10 }}>
          <span className="label" style={{ color: "var(--fg-3)" }}>
            What you get
          </span>
          <ul
            style={{
              margin: 0,
              paddingLeft: 20,
              fontSize: 14,
              color: "var(--fg-2)",
              lineHeight: 1.7,
            }}
          >
            <li>14-day sandbox API key (rate-limited, test data only)</li>
            <li>Operator ID, sandbox API host, and a one-page integration guide</li>
            <li>The same six REST endpoints as production</li>
            <li>Provably-fair seed reveal for every test round</li>
          </ul>
        </Card>
      </Section>
    </>
  );
}

function SuccessView({ data }: { data: SandboxSuccess }) {
  const apiHost = data.sandboxApiHost ?? API_HOST;
  const curl = [
    `curl -X POST ${apiHost}/rgs/session/launch \\`,
    `  -H 'content-type: application/json' \\`,
    `  -H 'X-API-Key: ${data.apiKey}' \\`,
    `  -d '{"operatorId":"${data.operatorId}","playerId":"test-1","currency":"USDC"}'`,
  ].join("\n");

  return (
    <>
      <Seo
        title="Sandbox key issued · Vault Breaker Studios"
        description="Your Vault Breaker sandbox API key — copy it now, it's only shown once."
        path="/sandbox"
      />

      <Section>
        <Badge kind="win" style={{ marginBottom: 18 }}>
          KEY ISSUED
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(36px, 5vw, 56px)",
            lineHeight: 1.05,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Your sandbox key is ready.
        </h1>
        <p
          style={{
            fontSize: 16,
            color: "var(--fg-2)",
            marginTop: 14,
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          Copy it now —{" "}
          <strong style={{ color: "var(--gold-bright)" }}>
            the API key is only shown once
          </strong>
          . If you lose it, you'll have to request a new sandbox.
        </p>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 720 }}>
        <SectionHeading eyebrow="Credentials" title="Your sandbox" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <CredentialRow label="Operator ID" value={data.operatorId} mono first />
          <CredentialRow label="API key" value={data.apiKey} mono highlight />
          <CredentialRow label="Expires" value={data.expiresAt} />
          {data.sandboxApiHost ? (
            <CredentialRow label="Sandbox API host" value={data.sandboxApiHost} mono />
          ) : null}
        </Card>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 720 }}>
        <SectionHeading eyebrow="Try a curl" title="Open your first session" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "20px 22px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              overflowX: "auto",
            }}
          >
            {curl}
          </pre>
          <div
            style={{
              padding: "12px 22px",
              borderTop: "1px solid var(--line)",
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <CopyButton label="Copy curl" value={curl} />
          </div>
        </Card>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 720 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Link to="/integration" style={{ textDecoration: "none" }}>
            <Button variant="gold" style={{ padding: "14px 22px", fontSize: 15 }}>
              READ INTEGRATION DOCS →
            </Button>
          </Link>
          <Link to="/operator" style={{ textDecoration: "none" }}>
            <Button style={{ padding: "14px 22px", fontSize: 15 }}>
              OPEN OPERATOR CONSOLE →
            </Button>
          </Link>
        </div>
      </Section>
    </>
  );
}

function CredentialRow({
  label,
  value,
  mono,
  highlight,
  first,
}: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
  first?: boolean;
}) {
  return (
    <div
      style={{
        padding: "16px 22px",
        borderTop: first ? "none" : "1px solid var(--line)",
        display: "grid",
        gridTemplateColumns: "minmax(140px, 200px) 1fr auto",
        gap: 16,
        alignItems: "center",
      }}
    >
      <span className="label" style={{ color: "var(--fg-3)" }}>
        {label}
      </span>
      <span
        style={{
          fontFamily: mono ? "var(--font-mono)" : "var(--font-ui)",
          fontSize: mono ? 13 : 14,
          color: highlight ? "var(--gold-bright)" : "var(--fg-1)",
          wordBreak: "break-all",
        }}
      >
        {value}
      </span>
      <CopyButton label="Copy" value={value} />
    </div>
  );
}

function CopyButton({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function onClick(): Promise<void> {
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard) return;
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — silently no-op */
    }
  }
  return (
    <Button
      onClick={onClick}
      type="button"
      style={{ padding: "8px 12px", fontSize: 12, whiteSpace: "nowrap" }}
    >
      {copied ? "COPIED" : label.toUpperCase()}
    </Button>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg-2)" }}>
        {label}
        {required ? <span style={{ color: "var(--red)" }}> *</span> : null}
      </span>
      {children}
    </label>
  );
}
